import { Dispatch } from '@reduxjs/toolkit';
import { Socket } from 'socket.io-client';
import { SocketMessage } from '@utils/gameSockets';
import {
  incrementGamesBetCount,
  lotteryAgainGameFound,
  lotteryAllNumberPicked,
  lotteryAutoplayGameInfo,
  lotteryGameFinished,
  lotteryGameFound,
  lotteryGameStarted,
  lotteryNextTurn,
  lotteryPlayerJoined,
  lotteryTurnNumberPicked,
  setLotteryError,
  setLotteryInfo,
  setLotteryLoading,
  setLotteryTimer,
} from '../store';
import {
  GameRoom,
  LotteryAllNumberPicked,
  LotteryFinishedState,
  LotteryInfo,
  LotteryJoinedPlayers,
  LotteryTurnNextTurn,
  LotteryTurnNumbersPicked,
  LotteryTurnType,
} from '../types';
import { setGameFindingEnd, setGameRestarting } from '@app/store/slices/statesSlice';
import { ErrorMessage } from '@entities/error';

interface LotteryOnInfoResultMessage extends SocketMessage {
  data: LotteryInfo;
}

interface LotteryGameRoomResultMessage extends SocketMessage {
  data: { gameRoom: GameRoom };
}

interface LotteryGamePlayerJoinedMessage extends SocketMessage {
  data: LotteryJoinedPlayers;
}

interface LotteryGameFinishedMessage extends SocketMessage {
  data: LotteryFinishedState;
}

interface LotteryAllNumberPickedMessage extends SocketMessage {
  data: LotteryAllNumberPicked;
}

interface LotteryTuenNumbersPickedMessage extends SocketMessage {
  data: LotteryTurnNumbersPicked;
}

interface LotteryTurnNextTurnPickedMessage extends SocketMessage {
  data: LotteryTurnNextTurn;
}

interface AutoplayInfoMessage extends SocketMessage {
  data: {
    autoplayUuid: string;
    gameRoom: GameRoom;
    status?: 'insufficient_balance' | 'all_games_played' | 'in_progress' | 'stopped';
  };
}

export const lotteryOnMessage =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_token: string) => (dispatch: Dispatch, socket: Socket) => {
    const checkNoErr = (message: SocketMessage) => {
      if (!message.success) {
        dispatch(
          setLotteryError(
            message.errorCode
              ? ErrorMessage[message.errorCode as keyof typeof ErrorMessage]
              : JSON.stringify(message.data),
          ),
        );
        return false;
      }
      return true;
    };
    socket.on('lottery/info/result', (response: LotteryOnInfoResultMessage) => {
      if (checkNoErr(response)) {
        dispatch(setLotteryInfo(response.data));
      }
      dispatch(setLotteryLoading(false));
    });
    socket.on('lottery/find-game/result', (response: LotteryGameRoomResultMessage) => {
      const success = checkNoErr(response);
      if (success) {
        if (response.data.gameRoom.againGameId) {
          dispatch(
            lotteryAgainGameFound({
              gameRoom: response.data.gameRoom,
              againGameId: response.data.gameRoom.againGameId,
            }),
          );
          // если рестартим игру, нам необязательно знать id найденной игры
          dispatch(setGameFindingEnd({ gameId: undefined }));
        } else {
          dispatch(lotteryGameFound({ gameRoom: response.data.gameRoom }));
          // если запустили новую игру, запоминаем gameId, для того, чтобы если что, перейти на нее
          dispatch(setGameFindingEnd({ gameId: response.data.gameRoom.id }));
        }
        dispatch(incrementGamesBetCount(response.data.gameRoom.bet));
      } else {
        dispatch(setGameFindingEnd({ gameId: undefined }));
      }
      dispatch(setGameRestarting(false));
    });
    socket.on('lottery/player-joined', (response: LotteryGamePlayerJoinedMessage) => {
      if (checkNoErr(response)) {
        dispatch(lotteryPlayerJoined(response.data));
      }
    });
    socket.on('lottery/started', (response: LotteryGameRoomResultMessage) => {
      if (checkNoErr(response)) {
        dispatch(lotteryGameStarted(response.data.gameRoom));
        if (response.data.gameRoom.settings.turnType === LotteryTurnType.AllAtOnce) {
          dispatch(setLotteryTimer({ gameId: response.data.gameRoom.id, timer: 20 }));
        } else if (response.data.gameRoom.settings.turnType === LotteryTurnType.InTurn) {
          dispatch(setLotteryTimer({ gameId: response.data.gameRoom.id, timer: 10 }));
        }
      }
    });
    socket.on('lottery/game-finished', (response: LotteryGameFinishedMessage) => {
      if (checkNoErr(response)) {
        dispatch(lotteryGameFinished(response.data));
      }
    });
    socket.on('lottery-all/number-picked', (response: LotteryAllNumberPickedMessage) => {
      if (checkNoErr(response)) {
        dispatch(lotteryAllNumberPicked(response.data));
      }
    });
    socket.on('lottery-turn/next-turn', (response: LotteryTurnNextTurnPickedMessage) => {
      if (checkNoErr(response)) {
        dispatch(lotteryNextTurn(response.data));
      }
    });
    socket.on(
      'lottery-turn/numbers-picked',
      (response: LotteryTuenNumbersPickedMessage) => {
        if (checkNoErr(response)) {
          dispatch(lotteryTurnNumberPicked(response.data));
        }
      },
    );
    socket.on('autoplay/info', (response: AutoplayInfoMessage) => {
      if (checkNoErr(response)) {
        socket.emit('join/autoplay/info');

        if (response.data?.gameRoom && response.data?.status) {
          dispatch(
            lotteryAutoplayGameInfo({
              gameRoom: response.data.gameRoom,
              status: response.data.status,
            }),
          );
        }
      }
    });
  };
